/**
 * The playback runner: a tape driving the ordinary flight scene.
 *
 * The whole design of the VCR rests on one seam that already existed —
 * `createFlightScene(container, callbacks, { runner })` takes an
 * `EncounterRunner` instead of building one, which is how `/duel` mounts a
 * lockstep fight into the same world, cockpit and cameras as `/fly`. A replay
 * is a third source of ticks for that seam, so the Mission Recorder inherits
 * the terrain, the aircraft models, the smoke, the tracers and the HUD without
 * a line of renderer code.
 *
 * The one thing this runner does *not* honour is the live stick. `advance`'s
 * `inputs` argument is ignored on purpose: the sticks are the tape's, tick for
 * tick, and a viewer's keyboard must not be able to fly the recording. Camera
 * keys still work, because those never went through the runner. `activeInputs`
 * is how the painted cockpit (`scene-cockpit-report.ts`'s control-surface
 * deflection) learns the stick this ignored — otherwise it would fall back to
 * the frame loop's live keyboard read, which is always neutral here, and every
 * replayed flight would show a dead stick no matter how hard the recording was
 * hauled around (2026-08-23).
 *
 * The three differences from `sim-runner.ts` are all in policy rather than
 * mechanism:
 *
 * - ticks come from `ReplayPlayer`, which owns the trace and the seek index;
 * - `frameSeconds` is scaled by the playback rate before `planFixedSteps` sees
 *   it, reusing the time-compression mechanism rather than a second one
 *   (`playback-speed.ts`);
 * - a scrub resets the interpolation pair, because interpolating between the
 *   state before a seek and the state after it would draw one frame of an
 *   aeroplane crossing the map.
 */

import {
  FIXED_DT,
  MAX_CATCHUP_STEPS,
  ReplayPlayer,
  interpolationAlpha,
  planFixedSteps,
  type EncounterState,
  type GroundQuery,
  type MissionRun,
  type ReplayTape,
  type ReplayVerification,
} from "@red-opus/engine";
import { combatHudFrom } from "../flight/combat-hud.ts";
import { roundViews } from "../flight/encounter-setup.ts";
import {
  aircraftViewsOf,
  effectSpawnsOf,
  playerPoseOf,
  playerSimOf,
  targetViewsOf,
  type EffectSpawn,
} from "../flight/encounter-views.ts";
import { hudFrom } from "../flight/hud-snapshot.ts";
import { encounterMessages } from "../flight/sim-messages.ts";
import type { EncounterRunner } from "../flight/sim-runner.ts";

/** Where playback stands, for the transport bar to draw. */
export interface PlaybackPosition {
  readonly tick: number;
  readonly ticks: number;
  readonly playing: boolean;
  readonly ended: boolean;
}

/**
 * An `EncounterRunner` the flight scene can mount, plus the transport the VCR
 * bar drives it with.
 */
export interface PlaybackRunner extends EncounterRunner {
  readonly tape: ReplayTape;
  position(): PlaybackPosition;
  /** Ticks per real second, as a multiple of 1x. See `playback-speed.ts`. */
  setRate(rate: number): void;
  rate(): number;
  seek(tick: number): void;
  /** The divergence ledger — whether this build is still flying the recorded flight. */
  verification(): ReplayVerification;
  mission(): MissionRun | null;
}

interface PlaybackState {
  readonly player: ReplayPlayer;
  previous: EncounterState;
  current: EncounterState;
  accumulator: number;
  paused: boolean;
  rate: number;
  lastMessage: string;
}

/**
 * The catch-up ceiling for one rendered frame, scaled by the rate.
 *
 * Exactly `time-compression.ts`'s own rule — `TIME_COMPRESSION_MAX_STEPS` is
 * `MAX_CATCHUP_STEPS * TIME_COMPRESSION_FACTOR` for the same reason — because
 * the trap is the same one: a compressed `frameSeconds` handed an uncompressed
 * ceiling clamps every single frame, and the tape quietly plays at 1x while the
 * bar says 8x.
 *
 * Scaling the *budget* rather than picking a per-rate constant is what makes it
 * work on a slow frame as well as a fast one. `ceil(rate) + 1` looks sufficient
 * against a 60 Hz frame (8x owes eight ticks) and collapses on a 8 Hz one,
 * where 8x owes sixty — which is precisely the case a viewer scrubbing a heavy
 * encounter is in.
 */
export function playbackMaxSteps(rate: number): number {
  return Math.max(1, Math.ceil(MAX_CATCHUP_STEPS * rate));
}

/** What a frame's worth of replayed ticks produced beyond the new state itself. */
interface TickHarvest {
  readonly messages: string[];
  readonly effects: EffectSpawn[];
}

/**
 * Replays `steps` ticks, keeping the interpolation pair and the message latch
 * up to date. Stops early at the end of the tape, which is how the transport
 * bar learns that a tape has finished without polling for it.
 */
function runTicks(state: PlaybackState, steps: number, playerId: string): TickHarvest {
  const messages: string[] = [];
  const effects: EffectSpawn[] = [];
  for (let index = 0; index < steps; index++) {
    const before = state.player.encounter();
    if (state.player.advance(1) === 0) break;
    const after = state.player.encounter();
    state.previous = before;
    state.current = after;
    effects.push(...effectSpawnsOf(after));
    for (const message of encounterMessages(before, after, playerId)) {
      if (message === state.lastMessage) continue;
      state.lastMessage = message;
      messages.push(message);
    }
  }
  return { messages, effects };
}

export function createPlaybackRunner(tape: ReplayTape, ground?: GroundQuery): PlaybackRunner {
  const player = new ReplayPlayer(tape, ground);
  const state: PlaybackState = {
    player,
    previous: player.encounter(),
    current: player.encounter(),
    accumulator: 0,
    paused: true,
    rate: 1,
    lastMessage: "",
  };

  const alpha = () => interpolationAlpha(state.accumulator);
  const playerId = tape.playerId;

  /** Resets the interpolation pair to a single state, so nothing is tweened across a jump. */
  const settle = (): void => {
    state.current = state.player.encounter();
    state.previous = state.current;
    state.accumulator = 0;
  };

  return {
    tape,

    advance(frameSeconds, _inputs, maxSteps) {
      if (state.paused || state.player.ended) {
        return { steps: 0, messages: [], effects: [], clamped: false };
      }
      // The *larger* of what the caller budgeted and what this rate needs, not
      // the caller's alone. `flight-scene.ts` always passes one — its
      // time-compression ceiling, `MAX_CATCHUP_STEPS` when compression is off —
      // and it knows nothing about playback rates, so honouring it verbatim
      // silently clamps 8x back to 8 ticks a frame. Measured in a browser:
      // 8x and 1x advanced the tape at exactly the same speed until this took
      // the maximum instead.
      const ceiling = Math.max(maxSteps ?? 0, playbackMaxSteps(state.rate));
      const plan = planFixedSteps(state.accumulator, frameSeconds * state.rate, FIXED_DT, ceiling);
      state.accumulator = plan.accumulator;
      const harvest = runTicks(state, plan.steps, playerId);
      return { steps: plan.steps, messages: harvest.messages, effects: harvest.effects, clamped: plan.clamped };
    },

    pose: () => playerPoseOf(state.previous, state.current, playerId, alpha()),
    state: () => playerSimOf(state.current, playerId),
    // The tape's own stick for the tick `state()` just landed on — see
    // `EncounterRunner.activeInputs`'s doc for why the frame loop must prefer
    // this over the live keyboard during a replay.
    activeInputs: () => state.player.currentInputs(),
    hud: () => hudFrom(playerSimOf(state.current, playerId)),
    others: () => aircraftViewsOf(state.previous, state.current, playerId, alpha()),
    targets: () => targetViewsOf(state.current),
    rounds: () => roundViews(state.current),
    combat: () => combatHudFrom(state.current, playerId),
    encounter: () => state.current,
    mission: () => state.player.mission(),
    verification: () => state.player.verification(),

    paused: () => state.paused,
    setPaused: (paused) => {
      state.paused = paused;
    },

    rate: () => state.rate,
    setRate: (rate) => {
      state.rate = rate;
      // Whatever was banked was banked at the old rate; carrying it across
      // would spend it at the new one and jump the picture on the changeover.
      state.accumulator = 0;
    },

    seek: (tick) => {
      state.player.seek(tick);
      settle();
      // A scrub crosses events that were never shown. Clearing the last-message
      // latch means the first message *after* the scrub is reported even if the
      // same message was the last one shown before it.
      state.lastMessage = "";
    },

    position: () => ({
      tick: state.player.tick,
      ticks: state.player.ticks,
      playing: !state.paused && !state.player.ended,
      ended: state.player.ended,
    }),
  };
}
